import { useState, useEffect, useCallback } from 'react';
import { useOrganization } from '../context/OrganizationContext';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

/**
 * Hook to manage Advanced Analysis Allowance
 * 
 * Provides:
 * - Current allowance status
 * - Functions to check and use allowance
 * - State helpers (isWarning, isExhausted)
 */
export const useAnalysisAllowance = () => {
  const { activeOrganization } = useOrganization();
  const [allowance, setAllowance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch current allowance status
  const fetchAllowance = useCallback(async () => {
    if (!activeOrganization) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/organization/analysis-allowance`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setAllowance(data);
        setError(null);
      } else {
        console.error('Failed to fetch analysis allowance');
        setError('Failed to fetch allowance');
      }
    } catch (err) {
      console.error('Error fetching analysis allowance:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeOrganization]);
  
  // Use one analysis (call before executing scan)
  const useAnalysis = useCallback(async () => {
    if (!activeOrganization) {
      return { success: false, error: 'No organization selected' };
    }
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/organization/analysis-allowance/use`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        // Refresh allowance after use
        await fetchAllowance();
        return { success: true, data };
      } else {
        const errorData = await response.json();
        return { 
          success: false, 
          error: errorData.detail?.message || 'Failed to use analysis',
          data: errorData.detail
        };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [activeOrganization, fetchAllowance]);
  
  // Check if analysis can be run
  const canRunAnalysis = allowance?.can_run_analysis ?? false;
  
  // State helpers
  const isWarning = allowance?.state === 'warning';
  const isExhausted = allowance?.state === 'exhausted';
  const isNormal = allowance?.state === 'normal';
  
  // Fetch on mount and when organization changes
  useEffect(() => {
    fetchAllowance();
  }, [fetchAllowance]);
  
  return {
    allowance,
    loading,
    error,
    canRunAnalysis,
    isWarning,
    isExhausted,
    isNormal,
    fetchAllowance,
    useAnalysis
  };
};

export default useAnalysisAllowance;
